/**
 * Sound Composable for Vue Components
 * 
 * Provides reactive sound functionality to Vue components
 * with easy-to-use methods and settings management
 */

import { ref, computed } from 'vue'
import soundService from '@/services/sounds'

// Global reactive state
const soundEnabled = ref(soundService.isEnabled())
const soundVolume = ref(soundService.getVolume())

export function useSounds() {
  
  // Computed properties
  const isSoundEnabled = computed(() => soundEnabled.value)
  const currentVolume = computed(() => soundVolume.value)
  
  // Methods to control sound settings
  const enableSound = () => {
    soundEnabled.value = true
    soundService.setEnabled(true)
  }
  
  const disableSound = () => {
    soundEnabled.value = false
    soundService.setEnabled(false)
  }
  
  const toggleSound = () => {
    if (soundEnabled.value) {
      disableSound()
    } else {
      enableSound()
    }
  }
  
  const setVolume = (volume) => {
    const clampedVolume = Math.max(0, Math.min(1, volume))
    soundVolume.value = clampedVolume
    soundService.setVolume(clampedVolume)
  }
  
  // Sound playing methods
  const playSuccess = (options) => soundService.playSuccess(options)
  const playClick = (options) => soundService.playClick(options)
  const playError = (options) => soundService.playError(options)
  const playScan = (options) => soundService.playScan(options)
  const playPayment = (options) => soundService.playPayment(options)
  const playAddCart = (options) => soundService.playAddCart(options)
  const playRemove = (options) => soundService.playRemove(options)
  const playPrint = (options) => soundService.playPrint(options)
  
  // Generic play method
  const playSound = (soundName, options) => soundService.play(soundName, options)
  
  // Convenience methods for common UI interactions
  const playButtonClick = () => {
    if (soundEnabled.value) {
      playClick({ volume: 0.3 })
    }
  }
  
  const playTransactionComplete = () => {
    if (soundEnabled.value) {
      playSuccess({ volume: 0.8 })
    }
  }
  
  const playErrorNotification = () => {
    if (soundEnabled.value) {
      playError({ volume: 0.6 })
    }
  }
  
  const playItemAdded = () => {
    if (soundEnabled.value) {
      playAddCart({ volume: 0.4 })
    }
  }
  
  const playItemRemoved = () => {
    if (soundEnabled.value) {
      playRemove({ volume: 0.4 })
    }
  }
  
  const playBarcodeScan = () => {
    if (soundEnabled.value) {
      playScan({ volume: 0.5 })
    }
  }
  
  const playPaymentProcessed = () => {
    if (soundEnabled.value) {
      playPayment({ volume: 0.7 })
    }
  }
  
  const playPrintStarted = () => {
    if (soundEnabled.value) {
      playPrint({ volume: 0.5 })
    }
  }
  
  return {
    // State
    isSoundEnabled,
    currentVolume,
    
    // Settings
    enableSound,
    disableSound,
    toggleSound,
    setVolume,
    
    // Basic sounds
    playSuccess,
    playClick,
    playError,
    playScan,
    playPayment,
    playAddCart,
    playRemove,
    playPrint,
    playSound,
    
    // UI interaction sounds
    playButtonClick,
    playTransactionComplete,
    playErrorNotification,
    playItemAdded,
    playItemRemoved,
    playBarcodeScan,
    playPaymentProcessed,
    playPrintStarted
  }
}